import cloneDeep from 'clone-deep'
import { SettingKey } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { getAdminConfig, getAdminEmails, setAdminConfig } from 'services'
import { MultiSelect, ToggleButton } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { ILoanGlobalSettings } from './interface'

interface IProjectInfo {
  name: string
  title: string
  logo: string
}

interface IProps {
  globalSetting?: ILoanGlobalSettings
  setGlobalSetting: (value: ILoanGlobalSettings) => void
  setAction: (value: string) => void
}

export const NexysConnectionSettings = ({ globalSetting, setGlobalSetting, setAction }: IProps) => {
  const [adminEmailsList, setAdminEmailsList] = useState<Record<string, string>>({})
  const [nexysConnectionAdmin, setNexysConnectionAdmin] = useState<Record<string, boolean>>({})
  const [nexysConnections, setNexysConnections] = useState<Record<string, boolean>>({})
  const [projectsMap, setProjectsMap] = useState<IProjectInfo[]>([])

  useEffect(() => {
    // Fetch admin emails and account types for the multiselect options
    getAdminEmails().then((emails: string[]) => {
      const newOptions: Record<string, string> = {}
      Object.keys(accountTypes).forEach((key) => (newOptions[key] = accountTypes[key]))
      emails.forEach((email) => (newOptions[email] = email))
      setAdminEmailsList(newOptions)
    })
    getAdminConfig('projectsMap').then((projects) => {
      setProjectsMap(projects)
    })
  }, [])

  useEffect(() => {
    // Update local state when globalSetting changes
    if (globalSetting) {
      setNexysConnectionAdmin(globalSetting.nexysConnectionAdmin || {})
      setNexysConnections(globalSetting.nexysConnections || {})
    }
  }, [globalSetting])

  const onChangeNexysConnectionAdmin = async (key: string, value: Record<string, boolean>) => {
    if (!globalSetting) return
    setNexysConnectionAdmin(value)
    await saveSettings({ nexysConnectionAdmin: value })
  }

  const onChangeNexysConnections = async (key: string, value: boolean) => {
    if (!globalSetting) return
    const newConnections = cloneDeep(nexysConnections)
    newConnections[key] = value
    setNexysConnections(newConnections)
    await saveSettings({ nexysConnections: newConnections })
  }

  const saveSettings = async (
    updates: Partial<Pick<ILoanGlobalSettings, 'nexysConnectionAdmin' | 'nexysConnections'>>,
  ) => {
    if (!globalSetting) return
    setAction('saving')
    const newSettings = cloneDeep(globalSetting) as ILoanGlobalSettings
    Object.assign(newSettings, updates)

    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSettings)
    setGlobalSetting(newSettings)
    setAction('')
  }

  return (
    <div className="relative">
      <div className="input md:col-span-full mb-4">
        <RenderInput
          input={{ inputType: 'section', title: 'Nexys to Nexys Connection' }}
          Key="nexysConnectionSection"
          onChange={() => {}}
        />
      </div>

      <div className="input md:col-span-1">
        <MultiSelect
          id="nexysConnectionAdmin"
          title="Nexys to Nexys Connection Admin"
          key="nexysConnectionAdmin"
          options={adminEmailsList}
          value={nexysConnectionAdmin}
          defaultSelected={false}
          onChange={(value) => onChangeNexysConnectionAdmin('nexysConnectionAdmin', value)}
        />
      </div>

      <div className="input md:col-span-full mt-4">
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700">Manage Connections</label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(nexysConnections).map((projectId) => {
            const project = projectsMap.find((project) => project.name === projectId)

            return (
              <ToggleButton
                key={projectId}
                id={`nexysConnection_${projectId}`}
                title={project?.title || projectId}
                value={nexysConnections[projectId] || false}
                onChange={(value) => onChangeNexysConnections(projectId, value)}
                onBlur={() => {}}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
